/*
 * Real data-bound widgets — pull leaf metadata from the plant schema and
 * subscribe to live values via signalStore. Two starting members:
 *
 *   - `single-metric`  → big value + unit + optional sparkline
 *   - `trend-chart`    → embeds the LeafHistoryChart with a compact header
 *
 * Both share a `WidgetHeader` at the top so the visual rhythm across the
 * dashboard stays consistent regardless of widget type.
 */

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { VNode } from 'preact';
import maplibregl from 'maplibre-gl';
import type { GeoJSONSource, LngLatBoundsLike } from 'maplibre-gl';
import { useLocation } from 'preact-iso';
import type { AlarmDto, LeafDto, ListPlantsResponse, NodeDto, PlantSchemaResponse, PlantSummary } from '../api/types';
import type { WidgetInstance } from './layoutHost';
import { registerWidget, type WidgetRenderContext } from './widgetRegistry';
import { useLivePlant, type LeafValue } from '../realtime/signalStore';
import type { Signal } from '@preact/signals';
import { EChartsHistoryChart } from '../components/EChartsHistoryChart';
import '../components/EChartsHistoryChart.css';
import { classifyFreshness, freshnessLabelDe } from '../realtime/freshness';
import { getConnectionState } from '../realtime/hubClient';
import { activeAlarmsSignal } from '../realtime/alarmsStore';
import { themeSignal } from '../theme/theme';
import { api } from '../api/client';
import { historyPath, stepPath, toSeries, type LeafHistoryResponse, type SeriesPoint } from '../api/history';
import { formatLeafValue, isNumericType } from '../format/leafValue';

// ---------------------------------------------------------------------------
// Leaf lookup — walks the plant schema recursively. Cheap enough to call on
// every render (typical plants have ≤ 5000 leaves; the widget only renders
// when its config changes or the schema arrives).
// ---------------------------------------------------------------------------

function findLeaf(schema: PlantSchemaResponse | null, leafId: string): LeafDto | null {
  if (!schema || !leafId) return null;
  for (const device of schema.devices) {
    const hit = findLeafInNodes(device.nodes, leafId);
    if (hit) return hit;
  }
  return null;
}

function findLeafInNodes(nodes: NodeDto[], leafId: string): LeafDto | null {
  for (const node of nodes) {
    for (const leaf of node.leaves) {
      if (leaf.id === leafId) return leaf;
    }
    const nested = findLeafInNodes(node.children, leafId);
    if (nested) return nested;
  }
  return null;
}

// (Plant-wide flatten was here — dropped when the value-table switched to
// operator-picked leaf ids. If a future widget needs "walk every leaf",
// re-add it or use LeafPicker's flatten helper.)

// ---------------------------------------------------------------------------
// Shared WidgetHeader — displayName + symbol/type meta group. Sits at the
// top of every data-bound widget; presentational widgets have their own
// idea of a header (or none).
// ---------------------------------------------------------------------------

function WidgetHeader({
  leaf, title,
}: { leaf: LeafDto | null; title: string | null }) {
  return (
    <div class="hv-w-header">
      <span class="hv-w-header__dot" aria-hidden="true" />
      <div class="hv-w-header__labels">
        <div class="hv-w-header__title">
          {title || (leaf ? (leaf.displayName ?? leaf.symbol) : 'Kein Datenpunkt')}
        </div>
        {leaf && (
          <div class="hv-w-header__meta muted">
            <span class="mono">{leaf.symbol}</span>
            <span class="hv-w-header__type-badge">{leaf.valueType}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SingleMetric
// ---------------------------------------------------------------------------

interface SingleMetricConfig {
  title?: string;
  leafId?: string;
  showSparkline?: boolean;
  threshold?: number | null;
}

function SingleMetricWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as SingleMetricConfig;
  const leafId = typeof cfg.leafId === 'string' ? cfg.leafId : '';
  const leaf = findLeaf(ctx.plantSchema, leafId);

  const live = useLivePlant(ctx.plantId);
  const valueSignal = live?.leafSignal(leafId) ?? null;
  const value = valueSignal ? valueSignal.value : null;
  const history = live?.leafHistory(leafId) ?? null;
  const connection = getConnectionState().value;
  const freshness = classifyFreshness(value, connection);

  if (!leafId) {
    return (
      <div class="hv-w-single">
        <WidgetHeader leaf={null} title={cfg.title ?? null} />
        <div class="hv-w-single__empty muted">Datenpunkt wählen…</div>
      </div>
    );
  }
  if (!leaf) {
    return (
      <div class="hv-w-single">
        <WidgetHeader leaf={null} title={cfg.title ?? null} />
        <div class="hv-w-single__empty muted">
          Datenpunkt <code class="mono">{leafId.slice(0, 8)}…</code> gehört nicht zu dieser Anlage.
        </div>
      </div>
    );
  }

  const formatted = formatLeafValue(value?.v, leaf);
  const unit = leaf.unit ?? '';
  const showUnit = value !== null && unit && isNumericType(leaf.valueType);

  return (
    <div class="hv-w-single">
      <WidgetHeader leaf={leaf} title={cfg.title ?? null} />
      <div
        class="hv-w-single__body"
        title={value ? `${freshnessLabelDe(freshness.label)} — ${value.t}` : freshnessLabelDe(freshness.label)}
      >
        <span
          class={`hv-w-single__dot hv-w-single__dot--${freshness.tone}`}
          aria-hidden="true"
        />
        <span class="hv-w-single__value">{formatted}</span>
        {showUnit && <span class="hv-w-single__unit muted">{unit}</span>}
      </div>
      {cfg.showSparkline && history && history.length >= 2 && (
        <MiniSparkline data={history} />
      )}
    </div>
  );
}

// Small inline sparkline. Sized responsively (SVG stretches to container),
// bezier-smoothed, semi-transparent — the same visual language the DpViewer
// row-sparkline uses, just wider and heavier because a widget cell has room.
function MiniSparkline({ data }: { data: readonly number[] }) {
  const width = 100;
  const height = 32;
  const padY = 3;
  let mn = Infinity;
  let mx = -Infinity;
  for (const v of data) {
    if (v < mn) mn = v;
    if (v > mx) mx = v;
  }
  if (mx - mn < 1e-9) { mn -= 0.5; mx += 0.5; }
  const span = mx - mn;
  const n = data.length;
  const px = (i: number) => (i / (n - 1)) * (width - 2) + 1;
  const py = (i: number) => padY + (1 - (data[i] - mn) / span) * (height - 2 * padY);

  let d = `M${px(0).toFixed(1)},${py(0).toFixed(1)} `;
  for (let i = 1; i < n; i++) {
    const cx = px(i - 1);
    const cy = py(i - 1);
    const mx2 = (cx + px(i)) / 2;
    const my = (cy + py(i)) / 2;
    d += `Q${cx.toFixed(1)},${cy.toFixed(1)} ${mx2.toFixed(1)},${my.toFixed(1)} `;
  }
  d += `L${px(n - 1).toFixed(1)},${py(n - 1).toFixed(1)}`;

  return (
    <svg
      class="hv-w-single__spark"
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={d.trim()} class="hv-w-single__spark-line" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// TrendChart — reuses LeafHistoryChart. Its embedded header + window
// controls are compact enough to sit inside a widget cell without further
// wrapping; we render our own WidgetHeader above and let the chart's header
// carry the window buttons.
// ---------------------------------------------------------------------------

interface TrendChartConfig {
  title?: string;
  leafId?: string;
  windowSeconds?: number;
  showLegend?: boolean;
}

function TrendChartWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as TrendChartConfig;
  const leafId = typeof cfg.leafId === 'string' ? cfg.leafId : '';
  const leaf = findLeaf(ctx.plantSchema, leafId);

  if (!leafId) {
    return (
      <div class="hv-w-trend">
        <WidgetHeader leaf={null} title={cfg.title ?? null} />
        <div class="hv-w-trend__empty muted">Datenpunkt wählen…</div>
      </div>
    );
  }
  if (!leaf) {
    return (
      <div class="hv-w-trend">
        <WidgetHeader leaf={null} title={cfg.title ?? null} />
        <div class="hv-w-trend__empty muted">
          Datenpunkt <code class="mono">{leafId.slice(0, 8)}…</code> gehört nicht zu dieser Anlage.
        </div>
      </div>
    );
  }

  return (
    <div class="hv-w-trend">
      <WidgetHeader leaf={leaf} title={cfg.title ?? null} />
      {/* EChartsHistoryChart replaces the hand-rolled SVG chart (issue #13);
          same public props (leafId, leaf, onClose). The no-op onClose keeps
          the "×" harmless so operators can't accidentally destroy the
          widget's chart. */}
      <div class="hv-w-trend__chart">
        <EChartsHistoryChart
          leafId={leaf.id}
          leaf={leaf}
          onClose={() => { /* embedded: no-op */ }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultiTrend — one chart, N series. Fetches each leaf's history in parallel,
// draws each as its own path with a colour from a small chart palette, plus
// a legend on top. Uses a shared, autoscaled Y-axis; mixing dimensions is
// visually noisy but honest — the operator chose the leaves.
// History is the shape (M4) at the SVG width, drawn as step lines with gaps.
// ---------------------------------------------------------------------------

interface MultiTrendConfig {
  title?: string;
  leafIds?: string[];
  windowSeconds?: number;
}

/** viewBox width of the multi-trend SVG; also the shape resolution requested. */
const MULTI_TREND_WIDTH = 900;

const MULTI_TREND_PALETTE = [
  '#8dcc4a', // accent green
  '#38bdf8', // sky
  '#f59e0b', // amber
  '#ef4444', // red
  '#a78bfa', // violet
  '#22d3ee', // cyan
  '#ec4899', // pink
  '#facc15', // yellow
];

function MultiTrendWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as MultiTrendConfig;
  const leafIds = Array.isArray(cfg.leafIds) ? cfg.leafIds : [];
  const windowSec = cfg.windowSeconds ?? 3600;
  const leaves = useMemo(() => {
    return leafIds
      .map((id) => findLeaf(ctx.plantSchema, id))
      .filter((l): l is LeafDto => l !== null);
  }, [ctx.plantSchema, leafIds.join('|')]);

  const [series, setSeries] = useState<Record<string, SeriesPoint[] | null>>({});
  const [loading, setLoading] = useState(true);

  // Fetch each leaf's history in parallel whenever the leaf set or window
  // changes. Each response goes into its own map slot; failures land as
  // null and the chart just draws the rest.
  useEffect(() => {
    if (leaves.length === 0) { setSeries({}); setLoading(false); return; }
    const ctrl = new AbortController();
    setLoading(true);
    const to = new Date();
    const from = new Date(to.getTime() - windowSec * 1000);
    const request = { mode: 'shape', widthPx: MULTI_TREND_WIDTH } as const;
    Promise.all(
      leaves.map((leaf) =>
        api.get<LeafHistoryResponse>(historyPath(leaf.id, from, to, request), ctrl.signal)
          .then((r) => [leaf.id, toSeries(r)] as const)
          .catch(() => [leaf.id, null] as const),
      ),
    ).then((entries) => {
      if (ctrl.signal.aborted) return;
      const map: Record<string, SeriesPoint[] | null> = {};
      for (const [id, r] of entries) map[id] = r;
      setSeries(map);
      setLoading(false);
    });
    return () => ctrl.abort();
  }, [leaves.map((l) => l.id).join('|'), windowSec]);

  if (!ctx.plantSchema) {
    return (
      <div class="hv-w-multitrend">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Multi Trend'} />
        <div class="hv-w-multitrend__empty muted">Schema wird geladen…</div>
      </div>
    );
  }
  if (leafIds.length === 0) {
    return (
      <div class="hv-w-multitrend">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Multi Trend'} />
        <div class="hv-w-multitrend__empty muted">Noch keine Datenpunkte gewählt.</div>
      </div>
    );
  }
  if (leaves.length === 0) {
    return (
      <div class="hv-w-multitrend">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Multi Trend'} />
        <div class="hv-w-multitrend__empty muted">Die gewählten Datenpunkte gehören nicht zu dieser Anlage.</div>
      </div>
    );
  }

  return (
    <div class="hv-w-multitrend">
      <WidgetHeader leaf={null} title={cfg.title ?? 'Multi Trend'} />
      <div class="hv-w-multitrend__legend">
        {leaves.map((leaf, i) => (
          <span class="hv-w-multitrend__legend-item" key={leaf.id}>
            <span
              class="hv-w-multitrend__legend-swatch"
              style={{ background: MULTI_TREND_PALETTE[i % MULTI_TREND_PALETTE.length] }}
            />
            <span class="hv-w-multitrend__legend-label" title={leaf.symbol}>
              {leaf.displayName ?? leaf.symbol}
            </span>
            {leaf.unit && <span class="hv-w-multitrend__legend-unit muted">{leaf.unit}</span>}
          </span>
        ))}
      </div>
      <div class="hv-w-multitrend__chart">
        {loading
          ? <div class="hv-w-multitrend__empty muted">Verlauf wird geladen…</div>
          : <MultiSeriesChart leaves={leaves} series={series} windowSec={windowSec} />}
      </div>
    </div>
  );
}

/**
 * SVG chart body — N smoothed lines sharing an autoscaled Y-axis and a
 * common time axis. No axes / gridlines yet (we can add them like the
 * single-leaf history chart if the density calls for it).
 */
function MultiSeriesChart({
  leaves, series, windowSec,
}: { leaves: LeafDto[]; series: Record<string, SeriesPoint[] | null>; windowSec: number }) {
  const width = MULTI_TREND_WIDTH;
  const height = 220;
  const padL = 36;
  const padR = 8;
  const padT = 6;
  const padB = 22;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  // Find shared X + Y ranges. If a series has no points we skip it in the
  // range computation; empty widget renders "no data" instead of a flat line.
  const now = Date.now();
  const tMin = now - windowSec * 1000;
  const tMax = now;
  const tSpan = Math.max(1, tMax - tMin);

  let yLo = Infinity;
  let yHi = -Infinity;
  for (const leaf of leaves) {
    for (const [, v] of series[leaf.id] ?? []) {
      if (v === null) continue;
      if (v < yLo) yLo = v;
      if (v > yHi) yHi = v;
    }
  }
  if (!Number.isFinite(yLo) || !Number.isFinite(yHi)) {
    return <div class="hv-w-multitrend__empty muted">Keine Werte im Zeitraum.</div>;
  }
  if (yHi - yLo < 1e-9) { yLo -= 1; yHi += 1; }
  const yPad = (yHi - yLo) * 0.08;
  yLo -= yPad;
  yHi += yPad;
  const ySpan = yHi - yLo;

  const xFor = (ms: number) => padL + ((ms - tMin) / tSpan) * plotW;
  const yFor = (v: number) => padT + (1 - (v - yLo) / ySpan) * plotH;

  const paths: { color: string; d: string }[] = leaves.map((leaf, i) => ({
    color: MULTI_TREND_PALETTE[i % MULTI_TREND_PALETTE.length],
    d: stepPath(series[leaf.id] ?? [], xFor, yFor),
  }));

  // Simple Y ticks (3 across the range).
  const yTicks = [yLo + ySpan * 0.1, yLo + ySpan * 0.5, yLo + ySpan * 0.9]
    .map((v) => ({ v, label: v.toFixed(Math.abs(v) >= 100 ? 0 : 1) }));

  return (
    <svg
      class="hv-w-multitrend__svg"
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Multi Trend Chart"
    >
      {yTicks.map((t) => (
        <g key={t.v}>
          <line
            x1={padL}
            y1={yFor(t.v)}
            x2={padL + plotW}
            y2={yFor(t.v)}
            class="hv-w-multitrend__gridline"
          />
          <text
            x={padL - 4}
            y={yFor(t.v) + 3}
            class="hv-w-multitrend__axis-label"
            text-anchor="end"
          >
            {t.label}
          </text>
        </g>
      ))}
      <rect x={padL} y={padT} width={plotW} height={plotH} class="hv-w-multitrend__frame" />
      {paths.map((p, i) => (
        p.d
          ? <path key={i} d={p.d} class="hv-w-multitrend__line" style={{ stroke: p.color }} />
          : null
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// ValueTable — a compact table over the plant's leaves. Config picks how
// many rows to show and which columns are visible; the widget subscribes
// to each visible leaf's signal so all values update live at the same
// cadence the DP-Viewer uses.
// ---------------------------------------------------------------------------

interface ValueTableConfig {
  title?: string;
  leafIds?: string[];
  columns?: string[];
}

const DEFAULT_TABLE_COLUMNS = ['displayName', 'symbol', 'value', 'unit', 'quality', 'timestamp'];
const KNOWN_COLUMNS = new Set(['symbol', 'displayName', 'value', 'unit', 'quality', 'timestamp']);

function ValueTableWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as ValueTableConfig;
  const leafIds = Array.isArray(cfg.leafIds) ? cfg.leafIds : [];
  const columns = (Array.isArray(cfg.columns) ? cfg.columns : DEFAULT_TABLE_COLUMNS)
    .filter((c) => KNOWN_COLUMNS.has(c));

  // Resolve configured leaf ids to leaves in the current plant schema.
  // Order follows the config array; ids not in this schema are silently
  // dropped (they belong to a different plant or were deleted).
  const rows = useMemo(() => {
    if (!ctx.plantSchema) return [];
    return leafIds
      .map((id) => findLeaf(ctx.plantSchema, id))
      .filter((l): l is LeafDto => l !== null);
  }, [ctx.plantSchema, leafIds.join('|')]);

  if (!ctx.plantSchema) {
    return (
      <div class="hv-w-table">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Werte-Tabelle'} />
        <div class="hv-w-table__empty muted">Schema wird geladen…</div>
      </div>
    );
  }
  if (leafIds.length === 0) {
    return (
      <div class="hv-w-table">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Werte-Tabelle'} />
        <div class="hv-w-table__empty muted">Noch keine Datenpunkte gewählt. Über das Zahnrad-Icon konfigurieren.</div>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div class="hv-w-table">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Werte-Tabelle'} />
        <div class="hv-w-table__empty muted">Die gewählten Datenpunkte gehören nicht zu dieser Anlage.</div>
      </div>
    );
  }
  if (columns.length === 0) {
    return (
      <div class="hv-w-table">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Werte-Tabelle'} />
        <div class="hv-w-table__empty muted">Keine Spalten sichtbar. Konfiguration prüfen.</div>
      </div>
    );
  }

  return (
    <div class="hv-w-table">
      <WidgetHeader leaf={null} title={cfg.title ?? 'Werte-Tabelle'} />
      <div class="hv-w-table__scroll">
        <table class="hv-w-table__table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} class={`hv-w-table__col hv-w-table__col--${col}`}>
                  {columnLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((leaf) => (
              <ValueTableRow key={leaf.id} leaf={leaf} columns={columns} plantId={ctx.plantId} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function columnLabel(col: string): string {
  switch (col) {
    case 'symbol': return 'Symbol';
    case 'displayName': return 'Name';
    case 'value': return 'Wert';
    case 'unit': return 'Einh.';
    case 'quality': return 'Qual.';
    case 'timestamp': return 'Zeit';
    default: return col;
  }
}

function ValueTableRow({
  leaf, columns, plantId,
}: { leaf: LeafDto; columns: string[]; plantId: string }) {
  const live = useLivePlant(plantId);
  const valueSignal = live?.leafSignal(leaf.id) ?? null;
  const value = valueSignal ? valueSignal.value : null;
  return (
    <tr>
      {columns.map((col) => {
        switch (col) {
          case 'symbol':
            return <td key={col} class="mono hv-w-table__cell hv-w-table__cell--symbol">{leaf.symbol}</td>;
          case 'displayName':
            return <td key={col} class="hv-w-table__cell">{leaf.displayName ?? '—'}</td>;
          case 'value':
            return <td key={col} class="hv-w-table__cell hv-w-table__cell--value">{formatLeafValue(value?.v, leaf)}</td>;
          case 'unit':
            return <td key={col} class="hv-w-table__cell muted">{leaf.unit ?? ''}</td>;
          case 'quality':
            return <td key={col} class="hv-w-table__cell hv-w-table__cell--quality">{value?.q ?? '—'}</td>;
          case 'timestamp':
            return <td key={col} class="hv-w-table__cell mono muted">{value ? shortTime(value.t) : '—'}</td>;
          default:
            return <td key={col} />;
        }
      })}
    </tr>
  );
}

function shortTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ---------------------------------------------------------------------------
// AlarmList — reads from the global activeAlarmsSignal (kept fresh by the
// SignalR alarm channel). Filters to the current plant + severity, sorts
// newest-first, truncates to maxRows.
// ---------------------------------------------------------------------------

interface AlarmListConfig {
  title?: string;
  maxRows?: number;
  severityFilter?: string[];
}

function AlarmListWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as AlarmListConfig;
  const all = activeAlarmsSignal.value;
  const severityFilter = new Set<string>(cfg.severityFilter ?? ['Warning', 'Alarm']);

  const rows = useMemo(() => {
    let list = all.filter((a) => a.plantId === ctx.plantId && a.state !== 'Cleared');
    if (severityFilter.size > 0) {
      list = list.filter((a) => severityFilter.has(a.severity));
    }
    list = list.slice().sort((a, b) => (a.triggeredAt < b.triggeredAt ? 1 : -1));
    return list.slice(0, cfg.maxRows ?? 25);
  }, [all, ctx.plantId, cfg.maxRows, cfg.severityFilter?.join('|')]);

  return (
    <div class="hv-w-alarms">
      <WidgetHeader leaf={null} title={cfg.title ?? 'Alarme'} />
      {rows.length === 0 ? (
        <div class="hv-w-alarms__empty muted">Keine aktiven Alarme.</div>
      ) : (
        <ul class="hv-w-alarms__list">
          {rows.map((a) => (
            <AlarmRow key={a.id} alarm={a} />
          ))}
        </ul>
      )}
    </div>
  );
}

function AlarmRow({ alarm }: { alarm: AlarmDto }) {
  const tone = alarm.severity === 'Alarm' ? 'error' : 'warn';
  return (
    <li class={`hv-w-alarms__row hv-w-alarms__row--${tone}`}>
      <span class={`hv-w-alarms__severity hv-w-alarms__severity--${tone}`}>
        {alarm.severity === 'Alarm' ? 'Alarm' : 'Warn'}
      </span>
      <span class="hv-w-alarms__msg">{alarm.message}</span>
      <span class="hv-w-alarms__time mono muted">{shortTime(alarm.triggeredAt)}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// GeoMap — mini MapLibre map with markers for every plant in scope. The
// dashboard is scoped to one plant, but the map shows the whole fleet so
// operators can jump plants without leaving the widget. Marker click routes
// to that plant's schema page.
// ---------------------------------------------------------------------------

interface GeoMapConfig {
  title?: string;
  initialZoom?: number;
  showLabels?: boolean;
}

// Selbst gehostete Protomaps-Vector-Tiles, gleicher Style wie in MapPage —
// siehe pages/mapStyle.ts fuer Details.
import { buildMapStyle } from '../pages/mapStyle';
// MapLibre center is [lng, lat] (GeoJSON order), unlike Leaflet's [lat, lng].
const MAP_FALLBACK_CENTER: [number, number] = [14.5, 47.5];

const GEO_SOURCE_ID = 'geo-plants';
const GEO_LAYER_POINTS = 'geo-plants-points';
const GEO_LAYER_LABELS = 'geo-plants-labels';

function readCssVarLocal(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

function GeoMapWidget({
  instance,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as GeoMapConfig;
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [plants, setPlants] = useState<PlantSummary[] | null>(null);

  // Kept in sync with `plants` so the styledata handler (a closure created at
  // map-init time) can read the current list on every reinstall. Without it a
  // theme swap drops the source and reinstalls it empty; the useEffect([plants])
  // below only reacts to list changes, so the plants stay hidden until reload.
  const plantsRef = useRef<PlantSummary[] | null>(null);
  plantsRef.current = plants;

  useEffect(() => {
    const ctrl = new AbortController();
    api.get<ListPlantsResponse>('/api/plants', ctrl.signal)
      .then((r) => setPlants(r.plants))
      .catch(() => setPlants([]));
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;
    const map = new maplibregl.Map({
      container: el,
      style: buildMapStyle(themeSignal.value),
      center: MAP_FALLBACK_CENTER,
      zoom: cfg.initialZoom ?? 7,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');

    const installLayers = () => {
      if (map.getSource(GEO_SOURCE_ID)) return;
      const accent = readCssVarLocal('--accent', '#3b82f6');
      const ring = readCssVarLocal('--surface-panel', '#ffffff');
      // Seed the fresh source with whatever plants are known right now.
      // On first install this is usually the empty list (fetch pending) and
      // the [plants] effect below fills it a tick later; on a theme swap
      // it's the already-loaded list, keeping the markers on screen.
      const current = plantsRef.current ?? [];
      const seedFeatures = current
        .filter((p): p is PlantSummary & { latitude: number; longitude: number } =>
          typeof p.latitude === 'number' && typeof p.longitude === 'number')
        .map((p) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] as [number, number] },
          properties: { id: p.id, displayName: p.displayName },
        }));
      map.addSource(GEO_SOURCE_ID, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: seedFeatures },
      });
      map.addLayer({
        id: GEO_LAYER_POINTS,
        type: 'circle',
        source: GEO_SOURCE_ID,
        paint: {
          'circle-color': accent,
          'circle-radius': 6,
          'circle-stroke-width': 3,
          'circle-stroke-color': ring,
        },
      });
      if (cfg.showLabels !== false) {
        map.addLayer({
          id: GEO_LAYER_LABELS,
          type: 'symbol',
          source: GEO_SOURCE_ID,
          layout: {
            'text-field': ['get', 'displayName'],
            'text-font': ['Noto Sans Regular'],
            'text-size': 11,
            'text-anchor': 'top',
            'text-offset': [0, 0.8],
            'text-optional': true,
          },
          paint: {
            'text-color': readCssVarLocal('--text-primary', '#111'),
            'text-halo-color': readCssVarLocal('--surface-base', '#fff'),
            'text-halo-width': 1.2,
          },
        });
      }

      map.on('click', GEO_LAYER_POINTS, (e) => {
        const feat = e.features?.[0];
        const id = feat?.properties?.id as string | undefined;
        if (id) location.route(`/plants/${id}/schema`);
      });
      map.on('mouseenter', GEO_LAYER_POINTS, () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', GEO_LAYER_POINTS, () => { map.getCanvas().style.cursor = ''; });
    };

    map.on('load', installLayers);
    map.on('styledata', installLayers);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Theme reactive style swap. MapLibre preserves view state across setStyle,
  // and our styledata handler reinstalls the source+layers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(buildMapStyle(themeSignal.value));
  }, [themeSignal.value]);

  // Markers reactive to plant list.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !plants) return;

    const withCoords = plants.filter(
      (p): p is PlantSummary & { latitude: number; longitude: number } =>
        typeof p.latitude === 'number' && typeof p.longitude === 'number',
    );
    const data = {
      type: 'FeatureCollection' as const,
      features: withCoords.map((p) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] as [number, number] },
        properties: { id: p.id, displayName: p.displayName },
      })),
    };

    const source = map.getSource(GEO_SOURCE_ID) as GeoJSONSource | undefined;
    if (source) source.setData(data);

    if (withCoords.length > 0) {
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      for (const p of withCoords) {
        if (p.longitude < minLng) minLng = p.longitude;
        if (p.latitude < minLat) minLat = p.latitude;
        if (p.longitude > maxLng) maxLng = p.longitude;
        if (p.latitude > maxLat) maxLat = p.latitude;
      }
      const bounds: LngLatBoundsLike = [[minLng, minLat], [maxLng, maxLat]];
      map.fitBounds(bounds, { padding: 24, maxZoom: 11, duration: 0 });
    }
  }, [plants, cfg.showLabels]);

  return (
    <div class="hv-w-geo">
      <WidgetHeader leaf={null} title={cfg.title ?? 'Karte'} />
      <div ref={containerRef} class="hv-w-geo__canvas" role="region" aria-label="Anlagen-Karte" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// EnergyFlow — sankey-flavoured flow diagram between sources and sinks.
// Each configured flow is a directed edge from a `fromLabel` node to a
// `toLabel` node, bound to one leaf. Node positions are auto-computed
// (topological columns), line thickness scales with the current value.
// ---------------------------------------------------------------------------

interface EnergyFlowConfig {
  title?: string;
  flows?: EnergyFlowEdge[];
}
interface EnergyFlowEdge {
  fromLabel: string;
  toLabel: string;
  leafId: string;
  unit?: string;
}

function EnergyFlowWidget({
  instance, ctx,
}: { instance: WidgetInstance; ctx: WidgetRenderContext }) {
  const cfg = instance.config as EnergyFlowConfig;
  const flows = Array.isArray(cfg.flows) ? cfg.flows : [];
  const live = useLivePlant(ctx.plantId);

  // Subscribe to each flow's leaf. Reading the signal here re-renders the
  // widget on any flow's value change; per-leaf fine-grained subscriptions
  // would be nicer but would need a redraw hook per SVG path, and this is
  // rare-write territory (few flows per widget).
  const values: Record<string, number | null> = {};
  for (const f of flows) {
    const s = live?.leafSignal(f.leafId);
    const v = s?.value?.v;
    values[f.leafId] = typeof v === 'number' && Number.isFinite(v) ? v : null;
  }

  if (!ctx.plantSchema) {
    return (
      <div class="hv-w-eflow">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Energie-Fluss'} />
        <div class="hv-w-eflow__empty muted">Schema wird geladen…</div>
      </div>
    );
  }
  if (flows.length === 0) {
    return (
      <div class="hv-w-eflow">
        <WidgetHeader leaf={null} title={cfg.title ?? 'Energie-Fluss'} />
        <div class="hv-w-eflow__empty muted">Noch keine Flüsse konfiguriert.</div>
      </div>
    );
  }

  // Two-column layout: distinct source-only labels on the left, distinct
  // sink-only labels on the right, and labels that are BOTH source and
  // sink go into a middle column. Simple heuristic, covers 95 % of the
  // real cases (Solar → Batterie → Haus, WP → Speicher → Verteiler, …).
  const fromSet = new Set(flows.map((f) => f.fromLabel));
  const toSet = new Set(flows.map((f) => f.toLabel));
  const leftLabels = flows
    .map((f) => f.fromLabel)
    .filter((l, i, arr) => arr.indexOf(l) === i && !toSet.has(l));
  const rightLabels = flows
    .map((f) => f.toLabel)
    .filter((l, i, arr) => arr.indexOf(l) === i && !fromSet.has(l));
  const middleLabels = Array.from(new Set(
    flows.flatMap((f) => [f.fromLabel, f.toLabel])
      .filter((l) => fromSet.has(l) && toSet.has(l)),
  ));

  const columns = middleLabels.length > 0
    ? [leftLabels, middleLabels, rightLabels]
    : [leftLabels, rightLabels];
  const nodePositions = new Map<string, { x: number; y: number }>();
  const width = 900;
  const height = 320;
  const marginX = 80;
  const marginY = 40;
  const colGap = columns.length > 1 ? (width - 2 * marginX) / (columns.length - 1) : 0;
  columns.forEach((col, ci) => {
    const x = marginX + ci * colGap;
    const gap = col.length > 1 ? (height - 2 * marginY) / (col.length - 1) : 0;
    col.forEach((label, ri) => {
      const y = col.length === 1
        ? height / 2
        : marginY + ri * gap;
      nodePositions.set(label, { x, y });
    });
  });

  // Line thickness: normalise against the largest absolute observed value
  // across all currently-known flows. Missing values render as a thin
  // dashed placeholder so the topology stays visible.
  const observed = Object.values(values).filter((v): v is number => v !== null);
  const peak = observed.length > 0 ? Math.max(...observed.map(Math.abs)) : 1;
  const strokeFor = (v: number | null) => {
    if (v === null) return 1;
    const norm = Math.min(1, Math.abs(v) / peak);
    return 1 + norm * 10; // 1..11 px
  };

  return (
    <div class="hv-w-eflow">
      <WidgetHeader leaf={null} title={cfg.title ?? 'Energie-Fluss'} />
      <div class="hv-w-eflow__canvas-wrap">
        <svg
          class="hv-w-eflow__svg"
          width="100%"
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="Energie-Fluss"
        >
          {flows.map((f, i) => {
            const from = nodePositions.get(f.fromLabel);
            const to = nodePositions.get(f.toLabel);
            if (!from || !to) return null;
            const v = values[f.leafId];
            const stroke = strokeFor(v);
            const cx = (from.x + to.x) / 2;
            const d = `M${from.x},${from.y} C${cx},${from.y} ${cx},${to.y} ${to.x},${to.y}`;
            const midX = cx;
            const midY = (from.y + to.y) / 2;
            const label = v !== null
              ? `${formatFlowValue(v)} ${f.unit ?? ''}`.trim()
              : '—';
            return (
              <g key={i}>
                <path
                  d={d}
                  class={`hv-w-eflow__edge${v === null ? ' hv-w-eflow__edge--unknown' : ''}`}
                  style={{ strokeWidth: stroke }}
                />
                <text
                  x={midX}
                  y={midY - stroke - 4}
                  class="hv-w-eflow__edge-label"
                  text-anchor="middle"
                >
                  {label}
                </text>
              </g>
            );
          })}
          {Array.from(nodePositions.entries()).map(([label, pos]) => (
            <g key={label} transform={`translate(${pos.x} ${pos.y})`}>
              <circle r={14} class="hv-w-eflow__node" />
              <text
                y={-22}
                class="hv-w-eflow__node-label"
                text-anchor="middle"
              >
                {label}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function formatFlowValue(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

// ---------------------------------------------------------------------------
// Registration. Every widget goes through the standard registry — the
// DashboardPage renders them by widgetTypeId with no special-casing.
// ---------------------------------------------------------------------------

registerWidget({
  id: 'single-metric',
  displayName: 'Single Metric',
  description: 'Aktueller Wert eines Datenpunkts, groß dargestellt, optional mit Sparkline.',
  render: (instance, ctx) => <SingleMetricWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'trend-chart',
  displayName: 'Trend Chart',
  description: 'Zeitreihen-Verlauf eines Datenpunkts über ein wählbares Zeitfenster.',
  render: (instance, ctx) => <TrendChartWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'multi-trend',
  displayName: 'Multi Trend',
  description: 'Zeitreihen mehrerer Datenpunkte in einem Diagramm. Legende oben, geteilte Y-Achse.',
  render: (instance, ctx) => <MultiTrendWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'value-table',
  displayName: 'Werte-Tabelle',
  description: 'Kompakte Tabelle aller Datenpunkte einer Anlage mit Live-Werten.',
  render: (instance, ctx) => <ValueTableWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'alarm-list',
  displayName: 'Alarm-Liste',
  description: 'Chronologische Liste aktiver Alarme der Anlage, filterbar nach Schwere.',
  render: (instance, ctx) => <AlarmListWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'geo-map',
  displayName: 'Karte',
  description: 'Karte mit allen Anlagen. Marker-Klick springt in die gewählte Anlage.',
  render: (instance, ctx) => <GeoMapWidget instance={instance} ctx={ctx} />,
});

registerWidget({
  id: 'energy-flow',
  displayName: 'Energie-Fluss',
  description: 'Sankey-artiges Flussdiagramm zwischen Quellen und Senken mit Live-Werten.',
  render: (instance, ctx) => <EnergyFlowWidget instance={instance} ctx={ctx} />,
});

// Convince the TS compiler that VNode + Signal + LeafValue are used — they
// only appear in type positions in the surrounding closures.
export type _WidgetSignalTypes = VNode | Signal<LeafValue | null>;
